/**
 * Номер телефона: то, что видит человек, и то, что ждёт бэк.
 *
 * Бэк принимает ровно `7XXXXXXXXXX` (`PhoneRequest`: `regex:/^7\d{10}$/`).
 * Человек же набирает как привык: с восьмёркой, с плюсом, со скобками.
 * Приводим здесь, а не в каждой форме — иначе одна из них рано или поздно
 * отправит `8707…` и получит 422 без объяснений.
 */

const DIGITS_IN_PHONE = 11;

/**
 * Только цифры, с кодом страны в казахстанском виде.
 *
 * Первая цифра — код страны, дальше десять цифр номера. Восьмёрку меняем
 * на семёрку сразу, с первого нажатия: «8 701…» набирают чаще, чем «7 701…»,
 * и ждать одиннадцатой цифры, чтобы поправить первую, значит показывать
 * человеку неверную маску всё время набора.
 *
 * Десятизначный номер сам по себе не достраиваем: у казахстанских мобильных
 * код оператора тоже начинается с семёрки, и отличить «701 123 45 67 без
 * кода страны» от начатого «7 011 23 45 67» нельзя — угадав, маска на глазах
 * переписала бы уже набранное.
 */
export function normalizePhone(input: string): string {
  const digits = input.replace(/\D/g, '');

  if (digits.startsWith('8')) {
    return `7${digits.slice(1, DIGITS_IN_PHONE)}`;
  }

  return digits.slice(0, DIGITS_IN_PHONE);
}

export function isCompletePhone(input: string): boolean {
  return /^7\d{10}$/.test(normalizePhone(input));
}

/** `77011234567` → `+7 (701) 123-45-67`. Незаконченный номер форматируем по мере ввода. */
export function formatPhone(input: string): string {
  const digits = normalizePhone(input);
  if (!digits) return '';

  let formatted = `+7 (${digits.slice(1, 4)}`;
  if (digits.length > 4) formatted += `) ${digits.slice(4, 7)}`;
  if (digits.length > 7) formatted += `-${digits.slice(7, 9)}`;
  if (digits.length > 9) formatted += `-${digits.slice(9, 11)}`;

  return formatted;
}

/**
 * Маска для поля ввода: `formatPhone` плюс нормальный Backspace.
 *
 * Сам `formatPhone` стереть номер не даёт: из `+7 (` Backspace делает `+7 `,
 * цифры те же, маска возвращает `+7 (` — поле залипает. Если строка стала
 * короче, а цифры не изменились, человек стёр символ маски (скобку, пробел,
 * дефис) и имел в виду цифру перед ним — её и убираем.
 */
export function formatPhoneInput(next: string, previous: string): string {
  const digits = normalizePhone(next);

  if (next.length < previous.length && digits === normalizePhone(previous)) {
    return formatPhone(digits.slice(0, -1));
  }

  return formatPhone(next);
}

/**
 * Поле WhatsApp в маске: пустое или уже `+7 (…`.
 *
 * Номер, заведённый до маски в чужом формате (`+49 151 …`), правится как
 * обычный текст: маска механически перекроила бы его цифры в `+7 (915) …`,
 * а проверка на полный казахстанский номер не дала бы сохранить реальный
 * контакт. Стёр поле целиком — дальше снова маска.
 */
export function isMaskedPhoneInput(value: string): boolean {
  return value === '' || value.startsWith('+7 (');
}

/** Ввод в поле WhatsApp: маска для казахстанского номера, чужой — как есть. */
export function formatWhatsappInput(next: string, previous: string): string {
  return isMaskedPhoneInput(previous) ? formatPhoneInput(next, previous) : next;
}

/**
 * Казахстанский номер целиком: одиннадцать цифр с семёркой или восьмёркой.
 * Строже `isCompletePhone`: лишние цифры не отрезаются, а считаются
 * признаком чужого номера.
 */
function isWholeKzPhone(input: string): boolean {
  return /^[78]\d{10}$/.test(input.replace(/\D/g, ''));
}

/**
 * Номер из базы → вид в поле с маской.
 *
 * Номер, заведённый до маски не в казахстанском виде, показываем как есть:
 * маска переписала бы `+49 151 …` в `+7 (915) …`, и партнёр сохранил бы
 * чужой номер, не заметив подмены.
 */
export function formatStoredPhone(stored: string | null | undefined): string {
  const value = (stored || '').trim();
  return isWholeKzPhone(value) ? formatPhone(value) : value;
}

/**
 * Номер из поля → в базу: `+77011234567`.
 *
 * Бэк хранит контакт строкой (`nullable|string|max:255`), а кабинет и
 * приложение строят ссылку `wa.me` по одним цифрам — единый вид без скобок
 * и пробелов ничего из этого не ломает. Не номер отдаём как ввели.
 */
export function toStoredPhone(input: string): string {
  const value = input.trim();
  return isWholeKzPhone(value) ? `+${normalizePhone(value)}` : value;
}
